import type { JSX } from 'react';
import { Skeleton } from '@/components/feedback';
import { PublicPageSection } from '@/layouts/PublicPageLayout';

type HeroContent = {
  eyebrow: string;
  title: string;
  description: string;
};

type HeroMediaContent = {
  label: string;
  location: string;
  ariaLabel: string;
  mediaType: 'image' | 'video';
};

export function HomeHeroSection({
  heroContent,
  heroMediaContent,
  heroPosterUrl,
  heroMediaUrl,
  shouldPlayHeroVideo,
  canShowPoster,
}: {
  heroContent: HeroContent;
  heroMediaContent: HeroMediaContent;
  heroPosterUrl: string;
  heroMediaUrl: string;
  shouldPlayHeroVideo: boolean;
  canShowPoster: boolean;
}): JSX.Element {
  const hasHeroMediaUrl = heroMediaUrl.length > 0;

  return (
    <PublicPageSection
      tone="base"
      aria-label="501 Alliance hero"
      id="about"
      className="relative overflow-hidden"
      stacked={false}
      containerClassName="grid gap-xl md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] md:items-center"
    >
      <header className="flex flex-col gap-md text-left">
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-accent">
          {heroContent.eyebrow}
        </p>
        <h1 className="text-4xl font-semibold text-brand-primary md:text-5xl">
          {heroContent.title}
        </h1>
        <p className="text-lg text-text-primary/85">{heroContent.description}</p>
      </header>

      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-3xl border border-brand-primary/10 shadow-card">
        {!hasHeroMediaUrl ? (
          <Skeleton className="h-full w-full" aria-label="Loading hero media" />
        ) : shouldPlayHeroVideo ? (
          <video
            src={heroMediaUrl}
            className="h-full w-full object-cover"
            poster={canShowPoster ? heroPosterUrl : undefined}
            autoPlay
            muted
            playsInline
            controls
            preload="metadata"
            aria-label={heroMediaContent.ariaLabel}
          />
        ) : heroMediaContent.mediaType === 'image' ? (
          <img
            src={heroMediaUrl}
            className="h-full w-full object-cover"
            alt={heroMediaContent.ariaLabel}
            decoding="async"
          />
        ) : canShowPoster ? (
          <img
            src={heroPosterUrl}
            className="h-full w-full object-cover"
            alt={heroMediaContent.ariaLabel}
            decoding="async"
          />
        ) : (
          <Skeleton className="h-full w-full" aria-label="Loading hero media" />
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col gap-xs bg-gradient-to-t from-black/70 via-black/0 to-transparent p-6 text-white">
          <span className="text-xs uppercase tracking-wide text-white/70">
            {heroMediaContent.label}
          </span>
          <span className="text-lg font-semibold">{heroMediaContent.location}</span>
        </div>
      </div>
    </PublicPageSection>
  );
}
